<template>
  <div class="video-feature-container" :class="spaceLevel">
    <div class="video-content">
      <!-- Fallback for browsers that don't support video -->
      <div v-if="!videoSupported" class="video-fallback" @click="openVideoInNewTab">
        <img 
          v-if="ad.poster" 
          :src="ad.poster" 
          :alt="ad.title || 'Video thumbnail'"
          class="fallback-thumbnail"
        />
        <div v-else class="fallback-placeholder">
          <i class="fas fa-video"></i>
          <p>Video content</p>
        </div>
        <div class="play-overlay">
          <i class="fas fa-play-circle"></i>
        </div>
      </div>
      <!-- Video element for supported browsers -->
      <video
        v-else
        ref="videoPlayer"
        :src="ad.url"
        :poster="ad.poster"
        :muted="isMuted"
        :autoplay="true"
        :loop="false"
        :controls="false"
        playsinline
        webkit-playsinline
        x5-playsinline
        class="video-element"
        @loadedmetadata="onVideoLoad"
        @error="onVideoError"
        @play="onVideoPlay"
        @ended="onVideoEnded"
      >
        Your browser does not support the video tag.
      </video>
      <div v-if="ad.title" class="video-overlay">
        <h6 class="video-title">{{ ad.title }}</h6>
      </div>
      <!-- Custom Mute/Unmute Button - only show for supported video -->
      <button 
        v-if="videoSupported"
        class="mute-toggle-btn" 
        @click.stop="toggleMute"
        :aria-label="isMuted ? 'Unmute' : 'Mute'"
      >
        <i 
          :class="isMuted ? 'fas fa-volume-mute' : 'fas fa-volume-up'" 
          class="volume-icon"
        ></i>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "VideoAd",
  props: {
    ad: {
      type: Object,
      required: true
    },
    spaceLevel: {
      type: String,
      default: 'normal'
    }
  },
  data() {
    return {
      index: 0,
      isMuted: true, // Start muted for autoplay to work
      videoSupported: true
    };
  },
  mounted() {
    // Check if browser supports video element
    this.videoSupported = !!document.createElement('video').canPlayType;
  },
  methods: {
    openVideoInNewTab() {
      // Navigate to ad's link URL if available (not the video file)
      const linkUrl = this.ad.link || this.ad.label;
      if (linkUrl) {
        window.open(linkUrl, '_blank', 'noopener,noreferrer');
      }
    },
    onVideoLoad(event) {
      const video = event.target;
      this.$emit('videoLoaded', {
        aspectRatio: video.videoWidth / video.videoHeight,
        type: 'video',
        duration: video.duration
      });
    },
    onVideoError() {
      this.$emit('videoError', {
        aspectRatio: 16/9, // Default aspect ratio
        type: 'video'
      });
    },
    onVideoPlay() {
      this.$emit('videoPlay');
    },
    onVideoEnded() {
      this.$emit('videoEnded');
    },
    toggleMute() {
      this.isMuted = !this.isMuted;
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.muted = this.isMuted;
      }
    }
  }
};
</script>

<style scoped>
.video-feature-container {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
}

.video-content {
  position: relative;
  width: 100%;
  height: 100%;
}

.video-carousel {
  height: 100%;
  width: 100%;
}

.video-slide-container {
  position: relative;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.video-element {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.video-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  padding: 20px 15px 15px;
  pointer-events: none;
}

.video-title {
  color: white;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

/* Mute/Unmute Button Styles */
.mute-toggle-btn {
  position: absolute;
  bottom: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.5);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  transition: opacity 0.3s ease, background-color 0.3s ease;
  z-index: 10;
}

.mute-toggle-btn:hover {
  opacity: 1;
  background-color: rgba(0, 0, 0, 0.7);
}

.mute-toggle-btn:focus {
  outline: 2px solid rgba(255, 255, 255, 0.5);
  outline-offset: 2px;
}

.volume-icon {
  font-size: 24px;
  color: white;
}

.video-carousel ::v-deep .carousel-inner {
  background-color: #000 !important;
  height: 100%;
}

.video-carousel ::v-deep .carousel-item {
  height: 100%;
}

.video-carousel ::v-deep .carousel-control-prev,
.video-carousel ::v-deep .carousel-control-next {
  background: rgba(0, 0, 0, 0.5);
  width: 40px;
  border-radius: 4px;
}

/* Responsive carousel controls based on space level */
.video-feature-container.minimal ::v-deep .carousel-indicators {
  display: none;
}

.video-feature-container.minimal ::v-deep .carousel-control-prev,
.video-feature-container.minimal ::v-deep .carousel-control-next {
  width: 30px;
  opacity: 0.7;
}

.video-feature-container.limited ::v-deep .carousel-control-prev,
.video-feature-container.limited ::v-deep .carousel-control-next {
  width: 35px;
}

.video-feature-container.normal ::v-deep .carousel-control-prev,
.video-feature-container.normal ::v-deep .carousel-control-next {
  width: 40px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .video-overlay {
    padding: 15px 10px 10px;
  }
  
  .video-title {
    font-size: 12px;
  }
  
  .video-feature-container ::v-deep .carousel-control-prev,
  .video-feature-container ::v-deep .carousel-control-next {
    width: 30px;
  }
}

/* Emergency fallback for very small screens */
@media (max-height: 500px) {
  .video-feature-container ::v-deep .carousel-indicators {
    display: none !important;
  }
  
  .video-feature-container ::v-deep .carousel-control-prev,
  .video-feature-container ::v-deep .carousel-control-next {
    width: 25px !important;
    opacity: 0.8 !important;
  }
}

/* Video Fallback Styles */
.video-fallback {
  position: relative;
  width: 100%;
  height: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
}

.fallback-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.fallback-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  opacity: 0.7;
}

.fallback-placeholder i {
  font-size: 48px;
  margin-bottom: 10px;
}

.fallback-placeholder p {
  margin: 0;
  font-size: 14px;
}

.play-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  transition: all 0.3s ease;
}

.play-overlay i {
  font-size: 50px;
  color: white;
  margin-left: 5px; /* Slightly offset to center the play icon visually */
}

.video-fallback:hover .play-overlay {
  background: rgba(0, 0, 0, 0.8);
  transform: translate(-50%, -50%) scale(1.1);
}

@media (max-width: 768px) {
  .play-overlay {
    width: 60px;
    height: 60px;
  }
  
  .play-overlay i {
    font-size: 35px;
  }
}
</style>